/* import __COLOCATED_TEMPLATE__ from './snapshot-item.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import Ember from 'ember';
import { post } from 'embercom/lib/ajax';
import type { Snapshot } from './side-sheet';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import type NotificationsService from 'embercom/services/notifications-service';
import { states } from 'embercom/models/data/matching-system/matching-constants';

interface Signature {
  Args: {
    snapshot: Snapshot;
    workflow: Workflow;
    editorState: EditorState;
    hasDraft: boolean;
    isViewOnly?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
  };
  Element: HTMLLIElement;
}

export default class SnapshotItem extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare store: Store;
  @service contentEditorService: any;

  get currentSnapshotStatusKey(): string {
    let state = this.contentEditorService.ruleset?.state;
    if (state === states.live) {
      return 'workflows.version-history.status-current';
    } else if (state === states.draft) {
      return 'workflows.version-history.status-draft';
    } else {
      return 'workflows.version-history.status-previously-live';
    }
  }

  get isCurrentSnapshotDraft(): boolean {
    return this.contentEditorService.ruleset?.state === states.draft;
  }

  get adminModel() {
    let adminId = this.args.snapshot.created_by_admin_id;
    if (!adminId) {
      return null;
    }
    return this.store.peekRecord('admin', adminId);
  }

  @tracked showRollbackModal = false;
  @tracked versionNote = '';

  get showRollbackButton() {
    return !this.args.isViewOnly && !this.args.snapshot.is_current && !this.args.snapshot.is_draft;
  }

  get isRollbackDisabled() {
    return this.args.hasDraft || taskFor(this.rollbackVersion).isRunning;
  }

  @action
  openRollbackModal() {
    this.versionNote = '';
    this.showRollbackModal = true;
  }

  @action
  closeRollbackModal() {
    this.showRollbackModal = false;
    this.versionNote = '';
  }

  @action
  updateVersionNote(event: Event) {
    this.versionNote = (event.target as HTMLTextAreaElement).value;
  }

  @action
  confirmRollback() {
    taskFor(this.rollbackVersion).perform();
  }

  @task({ drop: true })
  *rollbackVersion(): TaskGenerator<void> {
    try {
      yield post('/ember/operator_workflows/rollback_to_snapshot', {
        app_id: this.appService.app.id,
        workflow_instance_id: this.args.workflow.workflowInstanceId,
        snapshot_id: this.args.snapshot.id,
        version_note: this.versionNote || undefined,
      });

      this.showRollbackModal = false;
      this.notificationsService.notifyConfirmation(
        this.intl.t('workflows.version-history.rollback-success'),
      );

      if (!Ember.testing) {
        window.location.reload();
      }
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('workflows.version-history.rollback-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::VersionHistory::SnapshotItem': typeof SnapshotItem;
    'workflows/version-history/snapshot-item': typeof SnapshotItem;
  }
}
